/**
 * Contratos de dados do Mercado Prêmio.
 *
 * Estes tipos são a fronteira entre o frontend e o backend. O frontend inteiro
 * só conhece o que está declarado aqui — nenhum componente lê campo cru de
 * resposta HTTP. Ao plugar a API real, garanta que ela devolve estas formas
 * (ou adapte em `src/api/services/*`, que é o único lugar que toca o wire).
 */

/* ------------------------------------------------------------------ *
 * Produto
 * ------------------------------------------------------------------ */

/** Selos exibidos no card. `mais_vendido` é vermelho, `mais_buscado` é preto. */
export type ProductBadge = 'mais_vendido' | 'mais_buscado' | 'oferta_do_dia'

/** Origem do produto: sincronizado da API externa ou cadastrado no admin. */
export type ProductSource = 'api' | 'manual'

export interface ProductSpecItem {
  label: string
  value: string
}

export interface ProductSpecGroup {
  /** Título do grupo, ex.: "Características gerais". */
  title: string
  items: ProductSpecItem[]
}

export interface Product {
  id: string
  /** Usado na URL: /produto/:slug. Caia para `id` se o backend não gerar slug. */
  slug: string
  title: string

  categoryId: string | null
  categoryName: string | null

  /** Sempre em reais, como número. Formatação é responsabilidade da UI. */
  price: number
  /** Preço "de". Ausente quando não há desconto. */
  originalPrice?: number | null
  /** Inteiro de 0 a 100. Ausente quando não há desconto. */
  discountPercent?: number | null

  /** Primeira imagem é a principal. URLs absolutas (R2 ou CDN externa). */
  images: string[]

  /** 0 a 5, uma casa decimal. */
  rating?: number | null
  ratingCount?: number | null

  /** Quantidade vendida — exibida como "+ vendidos" quando presente. */
  soldCount?: number | null
  /** Estoque anunciado, exibido como "Quant. disponível". */
  availableQuantity?: number | null

  badges: ProductBadge[]

  /** Link do anúncio no marketplace de origem. É para lá que "Comprar agora" leva. */
  externalUrl: string

  /** Texto curto do bloco "Descrição da entrega". */
  deliveryDescription?: string | null
  /** Bullets do bloco "Sobre o item". */
  highlights: string[]
  specifications: ProductSpecGroup[]
  /** Texto livre do bloco "Outras informações". */
  description?: string | null

  source: ProductSource
  createdAt: string
  updatedAt: string
}

/** Versão enxuta usada em grids e carrosséis — evita trafegar specs à toa. */
export type ProductSummary = Pick<
  Product,
  | 'id'
  | 'slug'
  | 'title'
  | 'price'
  | 'originalPrice'
  | 'discountPercent'
  | 'images'
  | 'badges'
  | 'soldCount'
  | 'categoryName'
  | 'externalUrl'
>

/* ------------------------------------------------------------------ *
 * Categorias e home
 * ------------------------------------------------------------------ */

export interface Category {
  id: string
  name: string
  slug: string
  productCount?: number
}

export interface HeroBanner {
  id: string
  /** Imagem desktop (proporção ~3:1). */
  imageUrl: string
  /** Opcional: recorte vertical para telas pequenas. */
  mobileImageUrl?: string | null
  alt: string
  /** Destino ao clicar. Null = banner apenas decorativo. */
  href?: string | null
}

/** Uma "prateleira" da home: um título, um link de "ver tudo" e produtos. */
export interface Shelf {
  id: string
  title: string
  /** Rótulo do link à direita do título ("Ver todas as ofertas"/"Ver lista completa"). */
  seeAllLabel: string
  /** Rota interna de destino do link. */
  seeAllHref: string
  products: ProductSummary[]
}

export interface HomeContent {
  banners: HeroBanner[]
  /**
   * Card grande à esquerda. O título vira "Oferta do dia {nome do usuário}"
   * quando há sessão; `label` é o complemento usado para visitantes (opcional).
   */
  dailyOffer: { label?: string | null; product: ProductSummary } | null
  shelves: Shelf[]
}

/* ------------------------------------------------------------------ *
 * Busca
 * ------------------------------------------------------------------ */

export type SearchSort = 'relevance' | 'price_asc' | 'price_desc' | 'discount'

export interface SearchParams {
  q?: string
  categoryId?: string
  sort?: SearchSort
  /** Paginação por cursor. Ausente = primeira página. */
  cursor?: string | null
  limit?: number
}

export interface Paginated<T> {
  items: T[]
  /** Passe de volta em `cursor` para pedir a próxima página. Null = acabou. */
  nextCursor: string | null
  /** Total conhecido, quando o backend souber informar. */
  total?: number
}

/* ------------------------------------------------------------------ *
 * Usuário, sessão e perfil
 * ------------------------------------------------------------------ */

export interface User {
  id: string
  email: string
  /** Nome de exibição no cabeçalho e em "Oferta do dia {nome}". */
  displayName: string
  username?: string | null
  firstName?: string | null
  lastName?: string | null
  nickname?: string | null
  cpf?: string | null
  address?: string | null
  occupation?: string | null
  phone?: string | null
  /** Papel define o acesso às rotas /admin. */
  role: 'user' | 'admin'
  emailVerified: boolean
  createdAt: string
}

export interface AuthSession {
  user: User
  /** Se o backend usar cookie httpOnly, devolva `null` aqui e ignore o header. */
  accessToken: string | null
  expiresAt?: string | null
}

export interface LoginPayload {
  email: string
  password: string
  /** Token do Cloudflare Turnstile, quando o widget estiver ativo. */
  turnstileToken?: string
}

export interface RegisterPayload {
  name: string
  email: string
  password: string
  turnstileToken?: string
}

/** Campos editáveis em "Dados pessoais". Todos opcionais: envia-se só o alterado. */
export type ProfileUpdatePayload = Partial<
  Pick<
    User,
    | 'nickname'
    | 'cpf'
    | 'address'
    | 'firstName'
    | 'lastName'
    | 'occupation'
    | 'phone'
    | 'username'
  >
>

export interface ChangeEmailPayload {
  newEmail: string
}

export interface ChangePasswordPayload {
  currentPassword: string
  newPassword: string
}

/* ------------------------------------------------------------------ *
 * Compras registradas ("Adicionar compra" / "Já comprados")
 * ------------------------------------------------------------------ */

export interface Purchase {
  id: string
  product: ProductSummary
  /** ISO 8601. A lista da última semana é filtrada por este campo. */
  purchasedAt: string
  /** Cupom gerado pela compra, quando o backend emitir. */
  couponCode?: string | null
}

/**
 * Registro de compra. Envie `productId` quando vier do botão da sacola em um
 * card, ou `productUrl` quando vier do campo "Insira o link do produto".
 */
export interface CreatePurchasePayload {
  productId?: string
  productUrl?: string
}

/* ------------------------------------------------------------------ *
 * Administração
 * ------------------------------------------------------------------ */

/** Payload do formulário "Adicionar produto". Espelha os campos da tela. */
export interface AdminProductPayload {
  categoryId: string
  title: string
  price: number
  discountPercent: number
  quantity: number
  rating: number
  externalUrl: string
  deliveryDescription?: string
  /** Bullets de "Características". */
  highlights: string[]
  /** URLs já hospedadas no R2 — use `uploadProductImage` antes de salvar. */
  images: string[]
  description?: string
}

export interface AdminProductListItem {
  id: string
  title: string
  categoryName: string | null
  quantity: number
  price: number
  imageUrl: string | null
}

/** Resposta do upload de imagem para o R2. */
export interface UploadedImage {
  /** Chave do objeto no bucket, guardada no D1. */
  key: string
  /** URL pública servida pelo Worker/CDN. */
  url: string
}

/* ------------------------------------------------------------------ *
 * Erros
 * ------------------------------------------------------------------ */

/** Erro normalizado — toda falha de rede/API chega à UI neste formato. */
export class ApiError extends Error {
  readonly status: number
  /** Código estável do backend, ex.: "invalid_credentials". */
  readonly code: string
  /** Erros por campo, para destacar inputs no formulário. */
  readonly fieldErrors?: Record<string, string>

  constructor(
    message: string,
    status: number,
    code = 'unknown_error',
    fieldErrors?: Record<string, string>,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fieldErrors = fieldErrors
  }
}
